import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import type { ChapterEnum, WorkOrderChangeSeasonData, WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { destroyAgGridTableFilters } from 'utils';

import React, { forwardRef, useCallback, useMemo } from 'react';

import { useContractData } from '../../Contract/hooks/useContractData';
import { MatchMtrDialog } from '../../MatchMtrDialog';
import { AddMtrToWorkOrderChange } from '../AddMtrToWorkOrderChange';
import { AddRatioToWorkOrderChange } from '../AddRatioToWorkOrderChange';
import { getCellClass, getRowClass } from '../CellRenderers';
import {
    excludeTitleModalCancelBtnText,
    excludeTitleModalConfirmBtnText,
    excludeTitleModalConfirmText,
    excludeTitleModalTitle,
    removeTitleModalConfirmText,
    removeTitleModalTitle,
} from '../constants';
import { EditNoteModal } from '../EditNoteModal';
import { EditWorkOrderChangeMtrDialog } from '../EditWorkOrderChangeMtrDialog';
import { EditWorkOrderChangeRatio } from '../EditWorkOrderChangeRatio';
import { EditWorkOrderChangeUerDialog } from '../EditWorkOrderChangeUerDialog';
import { useWorkOrderChangeColumns, useWorkOrderChangeDeletePrices, useWorkOrderChangePriceList } from '../hooks';
import { useUpdateWorkOrderChangePlanVolume } from '../hooks/useUpdateWorkOrderChangePlanVolume';
import { MoveVolumesBetweenSeasons } from '../MoveVolumesBetweenSeasons/MoveVolumesBetweenSeasons';

import './WorkOrderChangePrice.css';

const defaultColDef = {
    cellClassRules: getCellClass,
};

interface IWorkOrderChangePrice {
    chapter: ChapterEnum;
    workOrderChangeId: number;
    constructionTypeId: number;
    refreshTable: () => void;
    isActionsDisabled: boolean;
    statusWorkOrderChange?: WorkOrderChangeStatusEnum;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    contractId: number;
    refreshProject: () => void;
}

export const WorkOrderChangePrice = forwardRef((props: IWorkOrderChangePrice, ref: any) => {
    const {
        chapter,
        workOrderChangeId,
        workOrderObjectId,
        workOrderChangeObjectId,
        refreshTable,
        isActionsDisabled,
        constructionTypeId,
        statusWorkOrderChange,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        contractId,
        refreshProject,
    } = props;

    const [contractData] = useContractData(contractId);

    const refreshWithFilterReset = useCallback(() => {
        refreshTable();
        destroyAgGridTableFilters({
            api: ref.current?.api,
            columns: ['code', 'name', 'status'],
        });
    }, [ref, refreshTable]);

    const { handleUpdateVolume } = useUpdateWorkOrderChangePlanVolume({ refreshTable });

    const tableKey = useMemo(
        () => `${chapter}_${workOrderObjectId}_${workOrderChangeObjectId}`,
        [chapter, workOrderChangeObjectId, workOrderObjectId],
    );

    const { dataSource, totalItems, fetchedItems, changeFilterHandler } = useWorkOrderChangePriceList({
        chapter,
        workOrderChangeId,
        workOrderObjectId,
        workOrderChangeObjectId,
        gridApiRef: ref,
    });

    const {
        fullSetColumns,
        resetCheckedRows,
        checkedRowsArr,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        isAddMtrModalOpened,
        isEditNoteModalOpened,
        closeEditNoteModalHandler,
        closeAddMtrModalHandler,
        isAddRatioModalOpenedAndStrategy,
        closeAddRatioModalHandler,
        checkedUerRow,
        fullSetSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
        isEditRatioModalOpened,
        closeEditRatioModal,
        isEditMtrModalOpened,
        closeEditMtrModalHandler,
        isEditUerModalOpened,
        closeEditUerModalHandler,
        rowData,
        closeMoveVolumesBetweenSeasonsForUer,
        isMoveVolumesBetweenSeasonsForUerOpen,
        isMatchMtrDialogOpen,
        closeMatchMtrDialogHandler,
    } = useWorkOrderChangeColumns({
        workOrderChangeId,
        isActionsDisabled,
        statusWorkOrderChange,
        handleUpdateVolume,
        workOrderObjectId,
        workOrderChangeObjectId,
        changeFilterHandler,
        chapter,
        gridApiRef: ref.current,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        refreshTable,
    });

    const { deleteData, deleteStatus, deleteError, deleteSetData, handleDelete, onSuccess } =
        useWorkOrderChangeDeletePrices({
            workOrderChangeId,
            priceIds: checkedRowsArr,
            resetCheckedRows,
            refreshTable: refreshWithFilterReset,
        });

    const onMatchMtrApply = useCallback(() => {
        refreshTable();
        closeMatchMtrDialogHandler();
    }, [closeMatchMtrDialogHandler, refreshTable]);

    const singleDeleteSuccess = useCallback(() => {
        onSuccess?.();
        closeSingleDeleteModalHandler();
    }, [closeSingleDeleteModalHandler, onSuccess]);

    const { removeModalTitle, removeModalText, removeModalConfirmBtn, removeModalCancelBtn } = useMemo(() => {
        if (checkedUerRow?.hasAcceptedCost === false) {
            return {
                removeModalTitle: excludeTitleModalTitle,
                removeModalText: excludeTitleModalConfirmText,
                removeModalConfirmBtn: excludeTitleModalConfirmBtnText,
                removeModalCancelBtn: excludeTitleModalCancelBtnText,
            };
        }

        return {
            removeModalTitle: removeTitleModalTitle,
            removeModalText: removeTitleModalConfirmText,
        };
    }, [checkedUerRow?.hasAcceptedCost]);

    const { checkedRowId, checkedWorkOrderChangePriceId, workOrderPriceId } = useMemo<{
        checkedRowId: number;
        checkedWorkOrderChangePriceId: number;
        workOrderPriceId: number;
    }>(
        () => ({
            workOrderPriceId: checkedUerRow?.workOrderPriceId,
            checkedRowId: checkedUerRow?.priceListId,
            checkedWorkOrderChangePriceId: checkedUerRow?.workOrderChangePriceId,
        }),
        [checkedUerRow?.priceListId, checkedUerRow?.workOrderChangePriceId, checkedUerRow?.workOrderPriceId],
    );

    return (
        <>
            <TableTopPanel
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                totalItems={totalItems}
                fetchedItems={fetchedItems}
                optionalColumns={fullSetSettingsColumns}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                showPinColumn
                tableApi={ref?.current}
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteError}
                    deleteSetData={deleteSetData}
                    handleDelete={handleDelete}
                    onSuccess={onSuccess}
                />
            </TableTopPanel>

            <div style={{ height: '100%' }} className="ag-theme-alpine WorkOrderChangePrice">
                <AgGridTable
                    datasource={dataSource}
                    columnDefs={fullSetColumns}
                    rowModelType="infinite"
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    defaultColDef={defaultColDef}
                    rowClassRules={getRowClass}
                    ref={ref}
                    context={{
                        tableUniqueId: tableKey,
                    }}
                    key={tableKey}
                />
            </div>

            {isEditUerModalOpened && (
                <EditWorkOrderChangeUerDialog
                    isOpened={isEditUerModalOpened}
                    closeModalHandler={closeEditUerModalHandler}
                    checkedUerRow={checkedUerRow}
                    constructionTypeId={constructionTypeId}
                    chapter={chapter}
                    refreshTable={refreshTable}
                    catalogName={contractData?.catalogUerBase?.name || ''}
                />
            )}

            {isEditRatioModalOpened && (
                <EditWorkOrderChangeRatio
                    isModalOpened={isEditRatioModalOpened}
                    closeModalHandler={closeEditRatioModal}
                    ratioId={checkedRowId}
                    checkedUerRow={checkedUerRow}
                    refreshTable={refreshTable}
                />
            )}

            {isAddMtrModalOpened && (
                <AddMtrToWorkOrderChange
                    isOpened={isAddMtrModalOpened}
                    closeModalHandler={closeAddMtrModalHandler}
                    resetCheckedRows={resetCheckedRows}
                    checkedRow={checkedRowsArr as any}
                    refreshTable={refreshWithFilterReset}
                    chapter={chapter}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                />
            )}

            {isEditNoteModalOpened && (
                <EditNoteModal
                    data={rowData}
                    isOpened={isEditNoteModalOpened}
                    closeModalHandler={closeEditNoteModalHandler}
                    refreshTable={refreshTable}
                />
            )}

            {isEditMtrModalOpened && (
                <EditWorkOrderChangeMtrDialog
                    isModalOpened={isEditMtrModalOpened}
                    closeModalHandler={closeEditMtrModalHandler}
                    mtrId={checkedRowId}
                    workOrderChangePriceId={checkedWorkOrderChangePriceId}
                    refreshTableDataHandler={refreshTable}
                    chapter={chapter}
                />
            )}

            {!!isAddRatioModalOpenedAndStrategy && (
                <AddRatioToWorkOrderChange
                    isOpened={!!isAddRatioModalOpenedAndStrategy}
                    strategy={isAddRatioModalOpenedAndStrategy}
                    closeModalHandler={closeAddRatioModalHandler}
                    resetCheckedRows={resetCheckedRows}
                    checkedUerRow={checkedUerRow as any}
                    refreshTable={refreshWithFilterReset}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                    contractId={contractId}
                />
            )}

            {isMoveVolumesBetweenSeasonsForUerOpen && (
                <MoveVolumesBetweenSeasons
                    onClose={closeMoveVolumesBetweenSeasonsForUer}
                    workOrderChangeId={workOrderChangeId}
                    chapter={chapter}
                    workOrderObjectId={workOrderObjectId}
                    workOrderChangeObjectId={workOrderChangeObjectId}
                    workOrderChangePriceId={checkedWorkOrderChangePriceId}
                    workOrderPriceId={workOrderPriceId}
                    refreshTableDataHandler={refreshTable}
                    workOrderChangeSeasons={workOrderChangeSeasons}
                />
            )}

            {isMatchMtrDialogOpen && (
                <MatchMtrDialog
                    contractId={contractId}
                    onMatchApply={onMatchMtrApply}
                    refreshProject={refreshProject}
                    chapter={chapter}
                    workOrderStatus={statusWorkOrderChange}
                    priceListId={checkedRowId}
                    workOrderPriceId={checkedWorkOrderChangePriceId}
                    refreshTable={refreshTable}
                    isChange
                />
            )}

            <DeleteItemFrom
                isOpened={isSingleDeleteModalOpened}
                closeDeleteModal={closeSingleDeleteModalHandler}
                deleteData={deleteData}
                deleteStatus={deleteStatus}
                deleteError={deleteError}
                deleteSetData={deleteSetData}
                handleDelete={handleDelete}
                handleSuccess={singleDeleteSuccess}
                modalTextHeader={removeModalTitle}
                modalTextConfirmation={removeModalText}
                modalCancelButtonText={removeModalCancelBtn}
                modalConfirmButtonText={removeModalConfirmBtn}
            />
        </>
    );
});

WorkOrderChangePrice.displayName = 'WorkOrderChangePrice';
